import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, BellRing, Building2, TrendingUp } from 'lucide-react';
import { api, hlaskaZChyby } from '../api/client.js';
import type { HlidaceZakazky, Objekt, Rekapitulace, Smlouva, Zakazka } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { formatKc } from '../lib/format.js';
import { SMLOUVA_TYP_LABEL, ZAKAZKA_STAV_BARVA, ZAKAZKA_STAV_LABEL } from '../lib/labels.js';
import { Btn, Chyba, Input, Karta, Nacitani, StatKarta, td, th } from '../components/ui.js';

type Zalozka = 'objekty' | 'smlouvy';

/** Detail zakázky — hub projektu: čísla (marže/změny/hlídače) + záložky. */
export function ZakazkaDetailPage() {
  const { id = '' } = useParams();
  const { maFinance } = useAuth();
  const [zalozka, setZalozka] = useState<Zalozka>('objekty');

  const {
    data: zakazka,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['zakazka', id],
    queryFn: () => api.get<Zakazka>(`/api/projects/${id}`),
  });
  // Čísla hubu (jen za zdí financí — bez práva se dlaždice nevykreslí).
  const { data: rekap } = useQuery({
    queryKey: ['rekapitulace', id],
    queryFn: () => api.get<Rekapitulace>(`/api/zakazky/${id}/rekapitulace`),
    enabled: maFinance && !!id,
  });
  const { data: hlidace } = useQuery({
    queryKey: ['hlidace', id],
    queryFn: () => api.get<HlidaceZakazky>(`/api/zakazky/${id}/hlidace`),
    enabled: maFinance && !!id,
  });

  if (isLoading) return <Nacitani />;
  if (error) return <Chyba>{hlaskaZChyby(error)}</Chyba>;
  if (!zakazka) return null;

  const pocetHlidacu = (hlidace?.alarmy.length ?? 0) + (hlidace?.pripominky.length ?? 0);

  return (
    <>
      {/* Masthead: titul + kontext vlevo, STAV jako razítko vpravo nahoře. */}
      <header className="flex items-start justify-between gap-4 border-b border-border pb-4">
        <div className="min-w-0 flex-1">
          <div className="text-sm text-text-secondary">
            <Link to="/zakazky" className="underline underline-offset-2">
              Zakázky
            </Link>{' '}
            / {zakazka.cislo}
          </div>
          <h1 className="mt-1 text-2xl font-bold">
            {zakazka.cislo} — {zakazka.nazev}
          </h1>
          <p className="text-text-secondary">
            {zakazka.objednatel ?? 'objednatel neuveden'}
            {zakazka.lokalita ? ` · ${zakazka.lokalita}` : ''}
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <div className={`rounded-xl px-4 py-2 text-center ${ZAKAZKA_STAV_BARVA[zakazka.stav]}`}>
            <p className="text-xs font-bold tracking-wide uppercase opacity-80">Stav</p>
            <p className="text-lg font-bold">{ZAKAZKA_STAV_LABEL[zakazka.stav]}</p>
          </div>
          {maFinance && (
            <Link
              to={`/zakazky/${zakazka.id}/rekapitulace`}
              className="text-sm font-semibold text-primary underline underline-offset-2"
            >
              Vícepráce a marže →
            </Link>
          )}
        </div>
      </header>

      {/* Hub projektu: čísla zakázky na jeden pohled (za zdí financí) —
          marže změn, rozpracované změny, hlídače, objekty; proklik na detail. */}
      {maFinance && rekap && (
        <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
          <Link to={`/zakazky/${zakazka.id}/rekapitulace`} className="block">
            <StatKarta
              popisek="Marže schválených změn"
              hodnota={formatKc(rekap.kumulativy.schvalene.marze)}
              detail={`${rekap.kumulativy.schvalene.pocet} schválených · výnos ${formatKc(rekap.kumulativy.schvalene.vynos)}`}
              ton={
                rekap.kumulativy.schvalene.pocet === 0
                  ? 'neutral'
                  : rekap.kumulativy.schvalene.marze?.startsWith('-')
                    ? 'error'
                    : 'success'
              }
              ikona={<TrendingUp size={18} strokeWidth={1.75} />}
            />
          </Link>
          <Link to="/zmeny" className="block">
            <StatKarta
              popisek="Změny v procesu"
              hodnota={rekap.kumulativy.vProcesu.pocet}
              detail={`výnos min. ${formatKc(rekap.kumulativy.vProcesu.vynosMin)}+`}
              ikona={<ArrowRightLeft size={18} strokeWidth={1.75} />}
            />
          </Link>
          <Link to={`/zakazky/${zakazka.id}/rekapitulace`} className="block">
            <StatKarta
              popisek="Aktivní hlídače"
              hodnota={pocetHlidacu}
              detail={
                (hlidace?.alarmy.length ?? 0) > 0
                  ? `${hlidace?.alarmy.length} alarm`
                  : 'žádný alarm'
              }
              ton={(hlidace?.alarmy.length ?? 0) > 0 ? 'error' : 'neutral'}
              ikona={<BellRing size={18} strokeWidth={1.75} />}
            />
          </Link>
          <StatKarta
            popisek="Objekty (SO)"
            hodnota={zakazka.objekty?.length ?? 0}
            detail={zakazka.lokalita ?? undefined}
            ikona={<Building2 size={18} strokeWidth={1.75} />}
          />
        </div>
      )}

      <div role="tablist" className="flex gap-2 border-b border-border">
        {(
          [
            ['objekty', `Objekty (${zakazka.objekty?.length ?? 0})`],
            ['smlouvy', 'Smlouvy zakázky'],
          ] as [Zalozka, string][]
        ).map(([klic, nazev]) => (
          <button
            key={klic}
            role="tab"
            aria-selected={zalozka === klic}
            onClick={() => setZalozka(klic)}
            className={`min-h-11 rounded-t-lg px-5 text-base font-semibold ${
              zalozka === klic
                ? 'border border-b-0 border-border bg-white text-primary'
                : 'text-text-secondary hover:text-text-primary'
            }`}
          >
            {nazev}
          </button>
        ))}
      </div>

      {zalozka === 'objekty' ? (
        <ObjektyTab zakazka={zakazka} />
      ) : (
        <SmlouvyTab zakazkaId={zakazka.id} />
      )}
    </>
  );
}

/** Objekty (SO xx) — inline CRUD: přidání, přejmenování, smazání. */
function ObjektyTab({ zakazka }: { zakazka: Zakazka }) {
  const { maPravo } = useAuth();
  const queryClient = useQueryClient();
  const smiPsat = maPravo('projekty.write');
  const [chyba, setChyba] = useState<string | null>(null);
  const [editovanyId, setEditovanyId] = useState<string | null>(null);
  const [editKod, setEditKod] = useState('');
  const [editNazev, setEditNazev] = useState('');
  const [novyKod, setNovyKod] = useState('');
  const [novyNazev, setNovyNazev] = useState('');

  const obnovit = () => queryClient.invalidateQueries({ queryKey: ['zakazka', zakazka.id] });

  const pridat = useMutation({
    mutationFn: () =>
      api.post<Objekt>(`/api/projects/${zakazka.id}/objekty`, { kod: novyKod, nazev: novyNazev }),
    onSuccess: () => {
      setNovyKod('');
      setNovyNazev('');
      setChyba(null);
      void obnovit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const ulozit = useMutation({
    mutationFn: (objektId: string) =>
      api.patch<Objekt>(`/api/projects/${zakazka.id}/objekty/${objektId}`, {
        kod: editKod,
        nazev: editNazev,
      }),
    onSuccess: () => {
      setEditovanyId(null);
      setChyba(null);
      void obnovit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const smazat = useMutation({
    mutationFn: (objektId: string) =>
      api.del<{ ok: boolean }>(`/api/projects/${zakazka.id}/objekty/${objektId}`),
    onSuccess: () => {
      setChyba(null);
      void obnovit();
    },
    onError: (err) => setChyba(hlaskaZChyby(err)),
  });

  const objekty = zakazka.objekty ?? [];

  return (
    <Karta nadpis="Objekty (SO)">
      {chyba && (
        <div className="mb-4">
          <Chyba>{chyba}</Chyba>
        </div>
      )}
      {objekty.length === 0 && (
        <p className="mb-4 text-text-secondary">
          Zakázka zatím nemá objekty. Objekty (SO 01…) se dají založit tady, nebo vzniknou
          automaticky při importu soupisu prací z oddílových řádků „SO xx".
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={`${th} w-40`}>Kód</th>
              <th className={th}>Název</th>
              {smiPsat && <th className={`${th} w-56`}>Akce</th>}
            </tr>
          </thead>
          <tbody>
            {objekty.map((o) =>
              editovanyId === o.id ? (
                <tr key={o.id}>
                  <td className={td}>
                    <Input
                      aria-label="Kód objektu"
                      value={editKod}
                      onChange={(e) => setEditKod(e.target.value)}
                    />
                  </td>
                  <td className={td}>
                    <Input
                      aria-label="Název objektu"
                      value={editNazev}
                      onChange={(e) => setEditNazev(e.target.value)}
                    />
                  </td>
                  <td className={td}>
                    <div className="flex gap-2">
                      <Btn disabled={ulozit.isPending} onClick={() => ulozit.mutate(o.id)}>
                        Uložit
                      </Btn>
                      <Btn varianta="secondary" onClick={() => setEditovanyId(null)}>
                        Zrušit
                      </Btn>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={o.id}>
                  <td className={`${td} font-semibold`}>{o.kod}</td>
                  <td className={td}>{o.nazev}</td>
                  {smiPsat && (
                    <td className={td}>
                      <div className="flex gap-2">
                        <Btn
                          varianta="secondary"
                          onClick={() => {
                            setEditovanyId(o.id);
                            setEditKod(o.kod);
                            setEditNazev(o.nazev);
                          }}
                        >
                          Upravit
                        </Btn>
                        <Btn
                          varianta="danger"
                          disabled={smazat.isPending}
                          onClick={() => smazat.mutate(o.id)}
                        >
                          Smazat
                        </Btn>
                      </div>
                    </td>
                  )}
                </tr>
              ),
            )}
            {smiPsat && (
              <tr>
                <td className={td}>
                  <Input
                    aria-label="Kód nového objektu"
                    placeholder="SO 01"
                    value={novyKod}
                    onChange={(e) => setNovyKod(e.target.value)}
                  />
                </td>
                <td className={td}>
                  <Input
                    aria-label="Název nového objektu"
                    placeholder="Hlavní hala"
                    value={novyNazev}
                    onChange={(e) => setNovyNazev(e.target.value)}
                  />
                </td>
                <td className={td}>
                  <Btn
                    disabled={pridat.isPending || novyKod === '' || novyNazev === ''}
                    onClick={() => pridat.mutate()}
                  >
                    Přidat objekt
                  </Btn>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Karta>
  );
}

/** Smlouvy zakázky — odkaz na detail; založení nové smlouvy. */
function SmlouvyTab({ zakazkaId }: { zakazkaId: string }) {
  const { maPravo, maFinance } = useAuth();

  const {
    data: smlouvy,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['smlouvy', zakazkaId],
    queryFn: () => api.get<Smlouva[]>(`/api/contracts?zakazkaId=${zakazkaId}`),
    enabled: maPravo('smlouvy.read'),
  });

  if (!maPravo('smlouvy.read')) {
    return (
      <Karta nadpis="Smlouvy zakázky">
        <p className="text-text-secondary">
          Na smlouvy nemáte oprávnění (chybí právo smlouvy.read).
        </p>
      </Karta>
    );
  }

  return (
    <Karta
      nadpis="Smlouvy zakázky"
      akce={
        maPravo('smlouvy.write') ? (
          <Link
            to={`/smlouvy/nova?zakazka=${zakazkaId}`}
            className="inline-flex min-h-11 items-center rounded-lg bg-primary px-4 font-medium text-white hover:bg-primary-hover"
          >
            Nová smlouva
          </Link>
        ) : undefined
      }
    >
      {isLoading && <Nacitani />}
      {error ? <Chyba>{hlaskaZChyby(error)}</Chyba> : null}
      {smlouvy && smlouvy.length === 0 && (
        <p className="text-text-secondary">
          Zakázka zatím nemá smlouvu. Založte hlavní SOD — bez ní nejde importovat soupis prací.
        </p>
      )}
      {smlouvy && smlouvy.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className={th}>Typ</th>
                <th className={th}>Číslo</th>
                <th className={th}>Protistrana</th>
                {maFinance && <th className={th}>Cena (bez DPH)</th>}
              </tr>
            </thead>
            <tbody>
              {smlouvy.map((s) => (
                <tr key={s.id}>
                  <td className={td}>{SMLOUVA_TYP_LABEL[s.typ]}</td>
                  <td className={td}>
                    <Link
                      to={`/smlouvy/${s.id}`}
                      className="font-semibold text-primary underline underline-offset-2"
                    >
                      {s.cislo ?? '(bez čísla)'}
                    </Link>
                  </td>
                  <td className={td}>{s.protistrana}</td>
                  {maFinance && <td className={td}>{formatKc(s.cena)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Karta>
  );
}
